import { createBrowserRouter, Navigate } from "react-router";

import { AdminNotificationsPage } from "./routes/admin/notifications";
import { AdminQaAutomationPage } from "./routes/admin/qa-automation";
import { ChangelogPage } from "./routes/changelog";
import {
  DataExplorerLandingPage,
  DynamoDataExplorerPage,
  PostgresDataExplorerPage,
  S3DataExplorerPage,
} from "./routes/data-explorer";
import { QaHighestFailureRatePage } from "./routes/admin/qa-automation/pages/highest-failure-rate";
import { QaRecentRunsPage } from "./routes/admin/qa-automation/pages/recent-runs";
import { QaRunDetailPage } from "./routes/admin/qa-automation/pages/run-detail";
import { QaSlowestTestsPage } from "./routes/admin/qa-automation/pages/slowest-tests";
import { AdminWorkflowsPage } from "./routes/admin/workflows";
import { WorkflowActiveRunsPage } from "./routes/admin/workflows/pages/active";
import { WorkflowDefinitionsPage } from "./routes/admin/workflows/pages/definitions";
import { WorkflowHistoryPage } from "./routes/admin/workflows/pages/history";
import { WorkflowInfraPage } from "./routes/admin/workflows/pages/infra";
import { WorkflowRunDetailPage } from "./routes/admin/workflows/pages/run-detail";
import { WorkflowRunsPage } from "./routes/admin/workflows/pages/runs";
import { WorkflowScheduledPage } from "./routes/admin/workflows/pages/scheduled";
import { ErrorPage } from "./routes/error-page";
import { HomePage } from "./routes/home";
import { InboxAnalyticsPage } from "./routes/inboxes";
import { NotFoundPage } from "./routes/not-found";
import { NotificationsLandingPage } from "./routes/admin/notifications/landing";
import { NotificationsAllPage } from "./routes/notifications-all";
import { ProfilePage } from "./routes/profile";
import { RootLayout } from "./routes/root-layout";
import { SettingsPage } from "./routes/settings";

// Plain data-router route objects (not JSX `<Route>` elements) — this is
// the shape both react-router's own object-router APIs and
// @tanstack/react-start's file-agnostic route config expect, so growing
// this tree (nested routes, `loader`s, etc.) later doesn't need a rewrite
// of the routing approach, just more entries.
//
// `errorElement` is set per LEAF route (not on the "/" route itself) so a
// render error only replaces that page's content inside `<RootLayout>`'s
// `<Outlet>` — the nav rail/header/identity control stay on screen instead
// of the whole shell disappearing. The `path: "*"` catch-all is a normal
// route match (not an error), so it renders inside the shell the same way.
export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      { index: true, element: <HomePage />, errorElement: <ErrorPage /> },
      {
        path: "workflows",
        element: <AdminWorkflowsPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "inboxes",
        element: <InboxAnalyticsPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "workflows/runs",
        element: <WorkflowRunsPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "workflows/runs/:runId",
        element: <WorkflowRunDetailPage />,
        errorElement: <ErrorPage />,
      },
      // Second entry for the same page/component — covers the
      // Diagnostics/Artefacts/Logs tabs (see `WorkflowRunDetailPage`'s own
      // doc comment for why this is two route objects instead of one
      // `:tab?` optional segment, which react-router v6 doesn't support).
      {
        path: "workflows/runs/:runId/:tab",
        element: <WorkflowRunDetailPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "workflows/active",
        element: <WorkflowActiveRunsPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "workflows/definitions",
        element: <WorkflowDefinitionsPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "workflows/history",
        element: <WorkflowHistoryPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "workflows/scheduled",
        element: <WorkflowScheduledPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "workflows/infra",
        element: <WorkflowInfraPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "qa-automation",
        element: <AdminQaAutomationPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "qa-automation/recent-runs",
        element: <QaRecentRunsPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "qa-automation/run/:runId",
        element: <QaRunDetailPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "qa-automation/run/:runId/results/:testId",
        element: <QaRunDetailPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "qa-automation/slowest-tests",
        element: <QaSlowestTestsPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "qa-automation/highest-failure-rate",
        element: <QaHighestFailureRatePage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "notifications",
        element: <NotificationsLandingPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "notifications/users",
        element: <AdminNotificationsPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "notifications/all",
        element: <NotificationsAllPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "data-explorer",
        element: <DataExplorerLandingPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "data-explorer/postgres",
        element: <PostgresDataExplorerPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "data-explorer/dynamodb",
        element: <DynamoDataExplorerPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "data-explorer/s3",
        element: <S3DataExplorerPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "changelog",
        element: <ChangelogPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: "settings",
        element: <Navigate to="/settings/me" replace />,
        errorElement: <ErrorPage />,
      },
      {
        path: "settings/me",
        element: <SettingsPage section="me" />,
        errorElement: <ErrorPage />,
      },
      {
        path: "settings/platform",
        element: <Navigate to="/settings/system" replace />,
        errorElement: <ErrorPage />,
      },
      {
        path: "settings/system",
        element: <SettingsPage section="system" />,
        errorElement: <ErrorPage />,
      },
      {
        path: "profile",
        element: <ProfilePage />,
        errorElement: <ErrorPage />,
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
